import { Injectable } from '@angular/core';
import { createStore, select, setProps, withProps } from '@ngneat/elf';
import { addEntities, entitiesPropsFactory, getAllEntities, getEntity, selectAllEntities, setEntities, withActiveId, withEntities } from '@ngneat/elf-entities';
import { persistState } from '@ngneat/elf-persist-state';
import { createRequestDataSource, createRequestsStatusOperator, initializeAsPending, updateRequestStatus, withRequestsStatus } from '@ngneat/elf-requests';
import { BookingDto, SaveBookingResult } from '@timechef/core/api';
import { LieuDto } from '@timechef/core/api/models/lieu-dto';
import { SlotDto } from '@timechef/core/api/models/slot-dto';
import { ensureDate } from '@timechef/core/utils';
import { capacitorStorageStrategy, includeKeys, selectAppRequestStatus } from '@timechef/core/utils/elf-utils';
import { combineLatest, map } from 'rxjs';
import { PageInfo, PageInfoWithData } from './booking.models';
import { computeBookings } from './booking.utils';

interface BookingProps {
  bookingsPageInfo: PageInfo;
  creationForm: {
    activeLieuId: number | null;
    slots: SlotDto[];
    creationResult: SaveBookingResult | null;
  };
  bookingInCalendar: boolean;
  bookingCalendarMaps: Record<number, any>;
}

export enum BookingStatusKey {
  Bookings = 'bookings',
  Lieux = 'lieux',
  Slots = 'slots',
  SaveBooking = 'save-booking',
  DeleteBooking = 'delete-booking',
  GeneratePdf = 'generate-pdf',
}

export interface Lieu {
  id: number;
  libelle: string;
}

@Injectable({ providedIn: 'root' })
export class BookingRepository {
  lieuxFactory = entitiesPropsFactory('lieux');

  private store = createStore(
    { name: 'booking' },
    withProps<BookingProps>({
      bookingsPageInfo: {
        hasMore: false,
        currentPageIndex: 0,
      },
      creationForm: { activeLieuId: null, slots: [], creationResult: null },
      bookingInCalendar: false,
      bookingCalendarMaps: {},
    }),
    withEntities<BookingDto>(),
    this.lieuxFactory.withLieuxEntities<LieuDto>(),
    withActiveId(),
    withRequestsStatus(initializeAsPending(BookingStatusKey.Bookings)),
    withRequestsStatus<BookingStatusKey.GeneratePdf>(),
  );

  generatePdfRequestStatus$ = this.store.pipe(selectAppRequestStatus(BookingStatusKey.GeneratePdf));

  instance = persistState(this.store, {
    key: 'booking',
    storage: capacitorStorageStrategy,
    source: () => this.store.pipe(includeKeys(['entities', 'lieuxEntities', 'ids', 'activeId', 'bookingInCalendar', 'bookingCalendarMaps'])), // keep bookingCalendarMaps in store after logout ?
  });

  bookingsByPeriodDS = createRequestDataSource({
    store: this.store,
    data$: () =>
      combineLatest({ bookings: this.store.pipe(selectAllEntities()), maps: this.store.pipe(select((s) => s.bookingCalendarMaps)) }).pipe(
        map(({ bookings, maps }) => computeBookings(new Date(), bookings, maps)),
      ),
    requestKey: BookingStatusKey.Bookings,
    dataKey: 'data',
    idleAsPending: true,
  });

  lieuxDS = createRequestDataSource({
    store: this.store,
    data$: () => this.store.pipe(selectAllEntities({ ref: this.lieuxFactory.lieuxEntitiesRef })),
    requestKey: BookingStatusKey.Lieux,
    dataKey: 'data',
    idleAsPending: true,
  });

  slotsDS = createRequestDataSource({
    store: this.store,
    data$: () => this.store.pipe(select((s) => s.creationForm.slots)),
    dataKey: 'data',
    requestKey: BookingStatusKey.Slots,
  });

  saveBookingDS = createRequestDataSource({
    store: this.store,
    data$: () => this.store.pipe(select((s) => s.creationForm.creationResult)),
    dataKey: 'data',
    requestKey: BookingStatusKey.SaveBooking,
  });

  bookingsPageInfo$ = this.store.pipe(select((s) => s.bookingsPageInfo));

  bookingsPageInfoWithData$ = this.store.pipe(
    select((s) => {
      const pageInfoWithData: PageInfoWithData<BookingDto> = {
        currentPageIndex: s.bookingsPageInfo.currentPageIndex,
        hasMore: s.bookingsPageInfo.hasMore,
        data: s.ids.map((id) => s.entities[id]),
      };
      return pageInfoWithData;
    }),
  );

  deleteBookingRequestStatus$ = this.store.pipe(selectAppRequestStatus(BookingStatusKey.DeleteBooking));

  getStoreSnapshot() {
    return this.store.query((s) => s);
  }

  getBookingCalendarIdWithTimechefId(id: number) {
    return this.store.query((s) => s.bookingCalendarMaps[id]);
  }

  getActiveLieu = () => {
    const curr = this.store.query((s) => s.creationForm.activeLieuId);
    return curr != null ? this.store.query(getEntity(curr, { ref: this.lieuxFactory.lieuxEntitiesRef })) : undefined;
  };

  setActiveLieu(lieuId: number) {
    this.store.update((s) => ({ ...s, creationForm: { ...s.creationForm, activeLieuId: lieuId } }));
  }

  getAddBookingInCalendar = () => this.store.query((s) => s.bookingInCalendar);

  setAddBookingInCalendar(bookingInCalendar: boolean) {
    this.store.update((s) => ({ ...s, bookingInCalendar }));
  }

  resetDeleteBookingStatus(): void {
    this.store.update(updateRequestStatus(BookingStatusKey.DeleteBooking, 'idle'));
  }

  updateLieux(lieux: LieuDto[]) {
    this.store.update(setEntities(lieux, { ref: this.lieuxFactory.lieuxEntitiesRef }), this.lieuxDS.setSuccess());
  }

  updateSlots(slots: SlotDto[]) {
    this.store.update((s) => ({ ...s, creationForm: { ...s.creationForm, slots } }), this.slotsDS.setSuccess());
  }

  updateSaveBookingResult(creationResult: SaveBookingResult) {
    this.store.update(this.saveBookingDS.setSuccess(), (s) => ({ ...s, creationForm: { ...s.creationForm, creationResult } }));
  }

  resetSaveBookingResult() {
    this.store.update(this.saveBookingDS.setSuccess());
  }

  getBookingsRequestStatus = () => this.store.query((s) => s.requestsStatus[BookingStatusKey.Bookings]);

  deleteBooking(id: number): void {
    this.store.update(updateRequestStatus(BookingStatusKey.DeleteBooking, 'success'), (s) => {
      const maps = { ...s.bookingCalendarMaps };
      if (maps[id]) {
        delete maps[id];
      }

      return {
        ...s,
        bookingCalendarMaps: maps,
      };
    });
  }

  getPaginatedBookings(): PageInfoWithData<BookingDto> {
    const entities = this.store.query(getAllEntities());
    return this.store.query((s) => {
      return {
        hasMore: s.bookingsPageInfo.hasMore,
        currentPageIndex: s.bookingsPageInfo.currentPageIndex,
        data: entities,
      };
    });
  }

  getDatesAlreadyBooked(): Date[] {
    return this.store.query(getAllEntities()).map((booking) => ensureDate(booking.date));
  }

  addBookings(bookings: BookingDto[], hasMore: boolean, currentPageIndex: number) {
    const clear = currentPageIndex === 0;
    bookings
      ? this.store.update(
          setProps({ bookingsPageInfo: { hasMore, currentPageIndex } }),
          clear ? setEntities(bookings) : addEntities(bookings),
          this.bookingsByPeriodDS.setSuccess(),
        )
      : this.store.update(setProps({ bookingsPageInfo: { hasMore, currentPageIndex } }), this.bookingsByPeriodDS.setSuccess());
  }

  addBookingCalendarMaps(maps: Record<number, any>) {
    this.store.update((s) => {
      const newVal = { ...s.bookingCalendarMaps, ...maps };

      return { ...s, bookingCalendarMaps: newVal };
    });
  }

  setPdfAsGenerated = () => this.store.update(updateRequestStatus(BookingStatusKey.GeneratePdf, 'success'));

  public trackRequestStatus = createRequestsStatusOperator(this.store);
}
